import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GroupNode,
  PlaceHolderNode,
  PocNodeType,
  PocTargetSearchType,
  TargetNode,
  UserNode,
} from './type';
import { PocApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import { validateEmail } from '../../../../utils/common';

function recoverTreeNodesAndCollectCheckedKeys(
  placeholderNodes: PlaceHolderNode[]
) {
  // collect all checked nodes
  // unchecked and unexpanded all nodes
  const checkedUserNodes: TargetNode[] = [];
  const ret: PlaceHolderNode[] = placeholderNodes.map((placeholderNode) => {
    return {
      ...placeholderNode,
      expanded: false,
      children: placeholderNode.children.map((node) => {
        if (node.type === PocNodeType.User && node.checked) {
          checkedUserNodes.push({
            key: node.key,
            title: node.title,
            target: node.target,
          });
        }

        if (node.type === PocNodeType.Group) {
          node.children.map((child) => {
            if (child.type === PocNodeType.User && child.checked) {
              checkedUserNodes.push({
                key: child.key,
                title: child.title,
                target: child.target,
              });
              child.checked = false;
            }
          });
        }

        if (node.type === PocNodeType.User) {
          return { ...node, checked: false };
        } else {
          return { ...node, expanded: false };
        }
      }),
    };
  });
  return [ret, checkedUserNodes] as const;
}

export const usePocTransferTree = (orgId: string) => {
  const { t } = useTranslation(['provision', 'policy']);

  const [sourceData, setSourceData] = useState<PlaceHolderNode[]>([]);
  const [targetData, setTargetData] = useState<TargetNode[]>([]);

  const targetKeysSetRef = useRef<Set<string>>(new Set());

  const handleToRightClick = useCallback(() => {
    const [newData, checkedUserNodes] =
      recoverTreeNodesAndCollectCheckedKeys(sourceData);
    setSourceData(newData);
    setTargetData([
      ...targetData,
      ...checkedUserNodes.filter((node) => {
        const isExist = targetKeysSetRef.current.has(node.key);
        if (isExist) {
          return false;
        }
        targetKeysSetRef.current.add(node.key);
        return true;
      }),
    ]);
  }, [sourceData, targetData]);

  const toggleNodeChecked = useCallback(
    (userKey: string) => {
      setSourceData(
        sourceData.map((node) => {
          node.children.forEach((child) => {
            if (child.type === PocNodeType.User && child.key === userKey) {
              child.checked = !child.checked;
            }

            if (child.type === PocNodeType.Group) {
              child.children.forEach((c) => {
                if (c.key === userKey) {
                  c.checked = !c.checked;
                }
              });
            }
          });
          return node;
        })
      );
    },
    [sourceData]
  );

  const toggleNodeExpanded = useCallback(
    async (groupKey: string) => {
      if (sourceData.some((node) => node.key === groupKey)) {
        setSourceData(
          sourceData.map((node) => {
            if (node.key === groupKey) {
              return {
                ...node,
                expanded: !node.expanded,
              };
            }
            return node;
          })
        );
        return;
      }
      const users: UserNode[] = (
        await PocApi.apiGetUsersByGroupKey(groupKey, orgId)
      ).map((user) => {
        return {
          ...user,
          type: PocNodeType.User,
          checked: false,
          children: null,
        };
      });
      setSourceData(
        sourceData.map((node) => {
          if (node.children.some((children) => children.key === groupKey)) {
            return {
              ...node,
              children: node.children.map((child) => {
                if (
                  child.key === groupKey &&
                  child.type === PocNodeType.Group
                ) {
                  return {
                    ...child,
                    expanded: !child.expanded,
                    children: users,
                  };
                }
                return child;
              }),
            };
          }
          return node;
        })
      );
    },
    [sourceData, orgId]
  );

  const onSearch = useCallback(
    async (keyword: string, mode: PocTargetSearchType) => {
      if (mode === PocTargetSearchType.TargetGroup && keyword.length < 3) {
        window.showNotification({
          severity: 'error',
          titleMsg: t(
            'provision:provision.service.exchange.poc.searchError.invalidGroup'
          ),
        });
        return;
      }
      if (mode === PocTargetSearchType.TargetUser && !validateEmail(keyword)) {
        window.showNotification({
          severity: 'error',
          titleMsg: t(
            'provision:provision.service.exchange.poc.searchError.invalidEmail'
          ),
        });
        return;
      }

      const children: UserNode[] | GroupNode[] = [];
      if (mode === PocTargetSearchType.TargetUser) {
        const res = await PocApi.apiSearchUserOrGroup(keyword, mode, orgId);
        if (res.length === 0) {
          window.showNotification({
            severity: 'error',
            titleMsg: t(
              'provision:provision.service.exchange.poc.searchError.noUser'
            ),
          });
          return;
        }
        res.forEach((item) =>
          (children as UserNode[]).push({
            ...item,
            checked: false,
            children: null,
          })
        );
      } else {
        const res = await PocApi.apiSearchUserOrGroup(keyword, mode, orgId);
        if (res.length === 0) {
          window.showNotification({
            severity: 'error',
            titleMsg: t(
              'provision:provision.service.exchange.poc.searchError.noGroup'
            ),
          });
          return;
        }
        res.forEach((item) =>
          (children as GroupNode[]).push({
            ...item,
            expanded: false,
            children: [],
          })
        );
      }

      const newData = [
        // put the result at head, maybe friendly
        {
          title: t('policy.policyTarget.search', { SEARCH: keyword }),
          key:
            t('policy.policyTarget.search', { SEARCH: keyword }) +
            Date.now().toString(),
          expanded: true,
          type: PocNodeType.PlaceHolder as const,
          children,
        },
        ...sourceData.map((v) => {
          return { ...v, expanded: false };
        }),
      ];

      setSourceData(newData);
    },
    [t, sourceData, orgId]
  );

  // right transfer box event handle
  const removeTargetDataByKey = useCallback(
    (key: string) => {
      setTargetData(targetData.filter((d) => d.key !== key));
    },
    [targetData]
  );

  useEffect(() => {
    targetKeysSetRef.current.clear();
    targetData.forEach((v) => targetKeysSetRef.current.add(v.key));
  }, [targetData]);

  return {
    sourceData,
    targetData,
    handleToRightClick,
    toggleNodeChecked,
    toggleNodeExpanded,
    onSearch,
    removeTargetDataByKey,
  };
};
